import { ArrowDown, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCompactMoney } from "@/utils/common.utils";
import type { OverviewStats as OverviewStatsData } from "@/types/overview.types";

interface OverviewStatsProps {
  /** Server-computed. Undefined only while the first fetch is in flight. */
  stats?: OverviewStatsData;
}

/** A signed count with an arrow, coloured by direction. Zero renders flat and
 *  grey — an arrow on "no change" reads as movement that didn't happen. */
function Delta({ value, suffix, tone = "positive" }: {
  value: number;
  suffix: string;
  tone?: "positive" | "negative";
}) {
  if (!value) return <span className="text-dash-faint">No change {suffix}</span>;
  const Icon = value > 0 ? ArrowUp : ArrowDown;
  const rising = value > 0;
  const good = tone === "positive" ? rising : !rising;
  return (
    <span className={`inline-flex items-center gap-1 ${good ? "text-dash-greenMid" : "text-dash-rust"}`}>
      <Icon className="size-4" />
      {rising ? "+" : ""}{value} {suffix}
    </span>
  );
}

export default function OverviewStats({ stats }: OverviewStatsProps) {
  const overdue = stats?.overdue_tasks;

  const tiles = [
    {
      label: "Active deals",
      value: stats?.active_deals.count ?? 0,
      detail: <Delta value={stats?.active_deals.delta_this_week ?? 0} suffix="this week" />,
    },
    {
      label: "Under contract",
      value: stats?.under_contract.count ?? 0,
      detail: `${formatCompactMoney(stats?.under_contract.volume ?? 0)} volume`,
    },
    {
      label: "Closing",
      value: stats?.closing_this_month.count ?? 0,
      detail: `${formatCompactMoney(stats?.closing_this_month.volume ?? 0)} volume this month`,
    },
    {
      label: "Overdue tasks",
      value: overdue?.count ?? 0,
      detail: (
        <span className="inline-flex items-center gap-1.5">
          {overdue?.critical_count
            ? <span className="text-dash-rust">{overdue.critical_count} closing soon</span>
            : <span className="text-dash-faint">None closing soon</span>}
          {!!overdue?.count && (
            <Link to="/dashboard" className="font-semibold text-dash-greenDeep hover:underline">
              Review
            </Link>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Brokerage overview" data-testid="dashboard-overview-stats">
      {tiles.map((tile) => (
        <Card key={tile.label} className="min-h-[144px] rounded-2xl border-dash-border shadow-none">
          <CardHeader className="px-5 pb-0 pt-5">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.14em] text-dash-faint">{tile.label}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 px-5 pb-5 pt-4">
            <p className="text-4xl font-bold leading-none tracking-tight text-dash-ink">{tile.value}</p>
            <div className="text-xs text-dash-faint">{tile.detail}</div>
          </CardContent>
        </Card>
      ))}
    </section>
  );
}
